import { useEffect, useState } from 'react';
import api from '../api';
import { auth, decodeJwt } from '../auth';
import { getTeamDisplayName } from '../teamDisplay';

export default function PlayForm() {
  const [form, setForm] = useState({
    personnel: '',
    formation: '',
    motion: '',
    action: '',
    nickname: '',
    type: '',
    pointOfAttack: '',
    situation: '',
  });
  const [team, setTeam] = useState('');
  const [message, setMessage] = useState('');

  const motionIsEmpty = !form.motion.trim() || /^no\s+motion$/i.test(form.motion.trim());
  const fullCall = [form.formation.trim(), motionIsEmpty ? '' : form.motion.trim(), form.action.trim()]
    .filter(Boolean)
    .join(' ');

  useEffect(() => {
    try {
      const token = auth?.token || localStorage.getItem('token') || '';
      if (!token) return;
      const claims = decodeJwt ? decodeJwt(token) : null;
      setTeam(getTeamDisplayName(claims));
    } catch {
      setTeam('');
    }
  }, []);

  function handleChange(e) {
    setForm({ ...form, [e.target.name]: e.target.value });
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setMessage('');

    try {
      await api.post('/api/plays', form);
      setMessage('Play saved.');
      setForm({
        personnel: '',
        formation: '',
        motion: '',
        action: '',
        nickname: '',
        type: '',
        pointOfAttack: '',
        situation: '',
      });
    } catch (err) {
      console.error('Submit error:', err);
      setMessage('Could not save the play. Check login and server.');
    }
  }

  return (
    <section className="surface-card">
      <div className="flex flex-col gap-4 border-b border-stone-200 pb-6 lg:flex-row lg:items-end lg:justify-between">
        <div>
          <p className="eyebrow">Add A Play</p>
          <h2 className="mt-3 text-4xl font-semibold text-brand-black">Build the call.</h2>
          <p className="mt-3 max-w-3xl text-lg leading-8 text-stone-700">
            Enter the call the same way your staff says it: formation, motion, then action. Add a
            nickname only when your staff actually uses one.
          </p>
        </div>
        <div className="rounded-lg bg-brand-offwhite px-4 py-3 text-base text-stone-700">
          Team: <span className="font-semibold text-brand-black">{team || 'Unavailable'}</span>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="mt-8 grid gap-5 lg:grid-cols-2">
        <div>
          <label className="form-label" htmlFor="formation">
            Formation
          </label>
          <input
            id="formation"
            name="formation"
            value={form.formation}
            onChange={handleChange}
            placeholder="Twins Right Split"
            className="input-field mt-2 text-lg"
          />
        </div>

        <div>
          <label className="form-label" htmlFor="motion">
            Motion
          </label>
          <input
            id="motion"
            name="motion"
            value={form.motion}
            onChange={handleChange}
            placeholder="Y Jet or No Motion"
            className="input-field mt-2 text-lg"
          />
        </div>

        <div>
          <label className="form-label" htmlFor="action">
            Action
          </label>
          <input
            id="action"
            name="action"
            value={form.action}
            onChange={handleChange}
            placeholder="Power 25 or Float 1881"
            className="input-field mt-2 text-lg"
          />
        </div>

        <div>
          <label className="form-label" htmlFor="nickname">
            Nickname, if you use one
          </label>
          <input
            id="nickname"
            name="nickname"
            value={form.nickname}
            onChange={handleChange}
            placeholder="Cherry Float"
            className="input-field mt-2 text-lg"
          />
        </div>

        <div className="rounded-lg border-2 border-stone-200 bg-brand-offwhite px-5 py-4 lg:col-span-2">
          <p className="form-label">Full call</p>
          <p className="mt-2 text-2xl font-semibold text-brand-black">
            {fullCall || 'Twins Right Split Y Jet Power 25'}
          </p>
          {form.nickname.trim() && (
            <p className="mt-2 text-base text-stone-700">Nickname: {form.nickname.trim()}</p>
          )}
        </div>

        <div>
          <label className="form-label" htmlFor="type">
            Run or pass
          </label>
          <input
            id="type"
            name="type"
            value={form.type}
            onChange={handleChange}
            placeholder="Run"
            className="input-field mt-2 text-lg"
          />
        </div>

        <div>
          <label className="form-label" htmlFor="personnel">
            Personnel
          </label>
          <input
            id="personnel"
            name="personnel"
            value={form.personnel}
            onChange={handleChange}
            placeholder="11"
            className="input-field mt-2 text-lg"
          />
        </div>

        <div>
          <label className="form-label" htmlFor="pointOfAttack">
            Point of attack
          </label>
          <input
            id="pointOfAttack"
            name="pointOfAttack"
            value={form.pointOfAttack}
            onChange={handleChange}
            placeholder="Off tackle"
            className="input-field mt-2 text-lg"
          />
        </div>

        <div className="lg:col-span-2">
          <label className="form-label" htmlFor="situation">
            When do you like this play?
          </label>
          <input
            id="situation"
            name="situation"
            value={form.situation}
            onChange={handleChange}
            placeholder="3rd and short"
            className="input-field mt-2 text-lg"
          />
        </div>

        <div className="lg:col-span-2 flex flex-col gap-4 border-t border-stone-200 pt-6 sm:flex-row sm:items-center sm:justify-between">
          <div className="text-base text-stone-700">
            After you save it, this play is available in the team library and can also be used by
            the mobile app during live play calling.
          </div>
          <button type="submit" className="btn-primary justify-center">
            Save play
          </button>
        </div>

        {message && (
          <div
            className={`lg:col-span-2 rounded-lg px-4 py-3 text-base ${
              message.includes('Could not')
                ? 'border border-rose-200 bg-rose-50 text-rose-700'
                : 'border border-emerald-200 bg-emerald-50 text-emerald-700'
            }`}
          >
            {message}
          </div>
        )}
      </form>
    </section>
  );
}
